import { createSelector } from 'reselect'
import { isBedbankItem } from 'lib/checkout/checkoutUtils'
import { sum } from 'lib/array/arrayUtils'

const getCheckoutCartBedbankRoomRateIds = (items: Array<App.Checkout.AnyItem>) => items.filter(isBedbankItem).map(item => item.roomRateId)

/**
 * Check if the cart has a property collect item
 * @param state - The state of the checkout cart
 * @returns true if the cart has a property collect item, false otherwise
 */
export const getCartHasPropertyCollectItem = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.checkout.sessions,
  (items, sessions) => {
    const bedbankRoomRateIds = getCheckoutCartBedbankRoomRateIds(items)
    // it is required to cross check rateIds from checkout cart items as the session data sometime contain more item than added to checkout cart items
    return Object.values(sessions)
      .some(session => session.data?.paymentMethod === 'propertyCollect' && bedbankRoomRateIds.includes(session.data.roomRateId))
  })

export const getPayAtPropertyAmount = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.checkout.sessions,
  (items, sessions) => {
    const bedbankRoomRateIds = getCheckoutCartBedbankRoomRateIds(items)

    const payAtPropertySessionItems = Object.values(sessions)
      .filter(session => session.data?.paymentMethod === 'propertyCollect' && bedbankRoomRateIds.includes(session.data.roomRateId))

    // There can be only one pay at property item in the cart
    return sum(payAtPropertySessionItems.map(session => session.data?.totals?.inclusive || 0))
  })

const getSessionPayAtPropertyTotal = (payAtPropertyTotals: App.PayAtPropertyTotals | undefined) => {
  return (payAtPropertyTotals?.inclusive ?? 0) + (payAtPropertyTotals?.propertyFees ?? 0)
}

type Session = {
  data: App.BedbankEnquiry | null
  state: 'loading' | 'updating' | 'error' | 'done' | 'updated'
}

/**
 * Get the pay at property details, Whether cart has at least one property collect item and if true then the pay at property totals
 * @param state - The state of the checkout cart
 * @returns the pay at property details if cart has at least one property collect item, otherwise { cartHasPropertyCollectItem: false }
 * payAtPropertyAmount - the pay at property amount in the current region currency
 * payAtPropertyAmountInPropertyCurrency - the pay at property amount in the property currency
 * checkInDate - the check in date in the format of DD MMM YYYY
 */
export const getPayAtPropertyDetails = createSelector(
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.checkout.sessions,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => state.checkout.cart.currencyCode,
  (cartHasPropertyCollectItem, items, sessions, currentCurrency, bedbankOffers, checkoutCurrency): {
    cartHasPropertyCollectItem: false
  } | {
    cartHasPropertyCollectItem: true
    payAtPropertyAmount: number
    payAtPropertyAmountInPropertyCurrency: number
    payAtPropertyCurrencyCode: string
    checkInDate: string
    propertyCountryCode: string
  } => {
    if (!cartHasPropertyCollectItem) {
      return {
        cartHasPropertyCollectItem: false,
      }
    }

    const bedbankRoomRateIds = getCheckoutCartBedbankRoomRateIds(items)

    const payAtPropertySessionItems = Object.values(sessions)
      .filter(session => session.data?.paymentMethod === 'propertyCollect' && bedbankRoomRateIds.includes(session.data.roomRateId))

    // There can be multiple items but all will be from same offer.
    const payAtPropertyFirstSessionItem = payAtPropertySessionItems[0]
    const checkoutCartItem = items.find(item => item.itemType === 'bedbankHotel' && item.roomRateId === payAtPropertyFirstSessionItem?.data?.roomRateId)
    const offer = checkoutCartItem?.itemType === 'bedbankHotel' ? bedbankOffers[checkoutCartItem.offerId] : undefined
    const propertyCountryCode = offer?.property.address.countryCode ?? 'AU'

    // find unique session rows inside payAtPropertySessionItems for given roomRateId
    const uniquePayAtPropertySessionItems = bedbankRoomRateIds.reduce((acc, roomRateId) => {
      const sessionItems = payAtPropertySessionItems.filter(session => session.data?.roomRateId === roomRateId)
      const sessionItem = sessionItems.findLast(session => session.data?.roomRateId === roomRateId)

      // check if sessionItem is defined and not already included in the acc, which could be the case for multi-room checkout
      // for multi-room checkout, single session can contain total price for multiple items.
      if (sessionItem && !acc.some(s => s.data?.sessionId === sessionItem.data?.sessionId)) {
        return [...acc, sessionItem]
      }

      return acc
    }, [] as Array<Session>)

    const payAtPropertyAmountInPropertyCurrency =
    sum(uniquePayAtPropertySessionItems, session => getSessionPayAtPropertyTotal(session.data?.payAtPropertyTotals))

    const payAtPropertyAmount = sum(uniquePayAtPropertySessionItems, session => session.data?.totals?.inclusive ?? 0)

    return {
      cartHasPropertyCollectItem: true,
      payAtPropertyAmount,
      checkInDate: payAtPropertyFirstSessionItem?.data?.checkIn!,
      payAtPropertyAmountInPropertyCurrency,
      payAtPropertyCurrencyCode: payAtPropertyFirstSessionItem?.data?.payAtPropertyTotals?.currencyCode ?? checkoutCurrency ?? currentCurrency,
      propertyCountryCode,
    }
  })
